import { ArrowLeft, Loader2 } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { TeamSelector } from "@/components/shared";
import { useApp } from "@/context/AppContext";
import { sowiftService } from "@/lib/services";
import type { SowiftProjectCreate } from "@/types";

export function SowiftNewProject() {
  const navigate = useNavigate();
  const { teams } = useApp();
  const defaultTeam = teams.find((t) => t.isEveryoneTeam) || teams[0];
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState<SowiftProjectCreate>({
    name: "",
    clientName: "",
    description: "",
    currency: "USD",
    teamId: defaultTeam?.id,
  });

  const updateField = <K extends keyof SowiftProjectCreate>(key: K, value: SowiftProjectCreate[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.clientName.trim()) return;

    try {
      setSaving(true);
      setError(null);
      const payload: SowiftProjectCreate = {
        ...form,
        teamId: form.teamId,
      };
      const response = await sowiftService.createProject(payload);
      navigate(`/apps/sowift/${response.data.uuid}`);
    } catch (err) {
      console.error("Failed to create project:", err);
      setError("Failed to create project. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <div className="shrink-0 p-6 border-b border-border-primary">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/apps/sowift")}
            className="p-1 text-content-tertiary hover:text-content-primary hover:bg-interactive-active rounded-sm transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-content-primary">New Project</h1>
            <p className="text-content-tertiary mt-1">Create a new SOW project</p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 overflow-auto p-6">
        <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">
          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">{error}</div>
          )}

          {/* Project Name */}
          <div>
            <label htmlFor="sowift-project-name" className="block text-sm font-medium text-content-secondary mb-1.5">
              Project Name <span className="text-red-400">*</span>
            </label>
            <input
              id="sowift-project-name"
              type="text"
              value={form.name}
              onChange={(e) => updateField("name", e.target.value)}
              placeholder="e.g. Acme Corp - Platform Redesign"
              className="w-full px-4 py-2.5 bg-surface-primary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
              required
            />
          </div>

          {/* Client Name */}
          <div>
            <label htmlFor="sowift-client-name" className="block text-sm font-medium text-content-secondary mb-1.5">
              Client Name <span className="text-red-400">*</span>
            </label>
            <input
              id="sowift-client-name"
              type="text"
              value={form.clientName}
              onChange={(e) => updateField("clientName", e.target.value)}
              placeholder="e.g. Acme Corporation"
              className="w-full px-4 py-2.5 bg-surface-primary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="sowift-description" className="block text-sm font-medium text-content-secondary mb-1.5">
              Description
            </label>
            <textarea
              id="sowift-description"
              value={form.description}
              onChange={(e) => updateField("description", e.target.value)}
              placeholder="Brief summary of the engagement..."
              rows={3}
              className="w-full px-4 py-2.5 bg-surface-primary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary resize-none"
            />
          </div>

          {/* Team */}
          <TeamSelector teamId={form.teamId} onTeamChange={(id) => updateField("teamId", id)} />

          {/* Financial Fields */}
          <div className="border-t border-border-primary pt-6">
            <h3 className="text-sm font-medium text-content-secondary mb-4">Financial Details (optional)</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label htmlFor="sowift-client-budget" className="block text-xs text-content-quaternary mb-1">
                  Client Budget
                </label>
                <input
                  id="sowift-client-budget"
                  type="number"
                  value={form.clientBudget ?? ""}
                  onChange={(e) => updateField("clientBudget", e.target.value ? Number(e.target.value) : undefined)}
                  placeholder="0"
                  className="w-full px-3 py-2 bg-surface-primary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary text-sm"
                />
              </div>
              <div>
                <label htmlFor="sowift-rom-low" className="block text-xs text-content-quaternary mb-1">
                  ROM Low
                </label>
                <input
                  id="sowift-rom-low"
                  type="number"
                  value={form.romLow ?? ""}
                  onChange={(e) => updateField("romLow", e.target.value ? Number(e.target.value) : undefined)}
                  placeholder="0"
                  className="w-full px-3 py-2 bg-surface-primary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary text-sm"
                />
              </div>
              <div>
                <label htmlFor="sowift-rom-high" className="block text-xs text-content-quaternary mb-1">
                  ROM High
                </label>
                <input
                  id="sowift-rom-high"
                  type="number"
                  value={form.romHigh ?? ""}
                  onChange={(e) => updateField("romHigh", e.target.value ? Number(e.target.value) : undefined)}
                  placeholder="0"
                  className="w-full px-3 py-2 bg-surface-primary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary text-sm"
                />
              </div>
            </div>
            <div className="mt-3">
              <label htmlFor="sowift-currency" className="block text-xs text-content-quaternary mb-1">
                Currency
              </label>
              <select
                id="sowift-currency"
                value={form.currency}
                onChange={(e) => updateField("currency", e.target.value)}
                className="px-3 py-2 bg-surface-primary border border-border-secondary rounded-lg text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary text-sm"
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
                <option value="CAD">CAD</option>
                <option value="AUD">AUD</option>
              </select>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-4">
            <button
              type="submit"
              disabled={saving || !form.name.trim() || !form.clientName.trim()}
              className="flex items-center gap-2 px-6 py-2.5 bg-brand-gradient text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Create Project
            </button>
            <button
              type="button"
              onClick={() => navigate("/apps/sowift")}
              className="px-6 py-2.5 bg-surface-secondary hover:bg-interactive-active border border-border-secondary rounded-lg text-sm font-medium text-content-primary transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
